import { useContext, useEffect, useRef } from "react";
import { AWSConnectProctorSharedEvents } from "@emed-labs/virtual-lab-models";
import { AWSConnectAgentContext } from "./AWSConnectAgentGlobalProvider";
import { SiteContainer } from "@emed/virtual-lab-ui-shared/ui/SiteContainer";
import { DeviceWizard } from "@emed/virtual-lab-ui-shared/ui/video/DeviceWizard";
import { Spinner } from "@emed/virtual-lab-ui-shared/ui/Spinner";
import { Trans } from "@emed/virtual-lab-ui-shared/providers/TranslationProvider";
import { ModalContext } from "@emed/virtual-lab-ui-shared/providers/ModalProvider";
import { useStateRef } from "@emed/virtual-lab-ui-shared/hooks/UseStateRef";
import { useLogger } from "@emed/virtual-lab-ui-shared/providers/LogProvider";

enum APPLICATION_STATE {
	"NONE" = "NONE",
	"INITIALIZING" = "INITIALIZING",
	"PROCTOR_CHILD_READY" = "PROCTOR_CHILD_READY",
	"INITIATE_PROCTOR_LOGIN" = "INITIATE_PROCTOR_LOGIN",
	"PROCTOR_CHILD_LOGGED_IN" = "PROCTOR_CHILD_LOGGED_IN",
	"APPLICATION_TIMEOUT" = "APPLICATION_TIMEOUT",
	"IFRAME_LOADED" = "IFRAME_LOADED"
}

const PROCTOR_APPLICATION_TIMEOUT = 20000;

export const GlobalProctorUI = () => {
	const GlobalProctor = useContext(AWSConnectAgentContext);
	const TransCtx = useContext(Trans.context);
	const ModalCtx = useContext(ModalContext);
	const logger = useLogger("providers.GlobalProctorUI");

	const [childState, setChildState, childStateRef] = useStateRef(APPLICATION_STATE.NONE);
	const [vlabAppUrl, setVlabAppUrl, vlabAppUrlRef] = useStateRef<string>();

	const vlabAppLoadedTimeout = useRef<NodeJS.Timeout>();

	useEffect(() => {
		DeviceWizard.popup(false, TransCtx.lookup);
		GlobalProctor.initializeAgent();
	}, []);

	useEffect(() => {
		return GlobalProctor.addListener(AWSConnectProctorSharedEvents.PROCTOR_CHILD_READY, () => {
			setChildState(APPLICATION_STATE.PROCTOR_CHILD_READY);
		});
	}, []);

	useEffect(() => {
		return GlobalProctor.addListener(AWSConnectProctorSharedEvents.INITIATE_PROCTOR_LOGIN, () => {
			setChildState(APPLICATION_STATE.INITIATE_PROCTOR_LOGIN);
		});
	}, []);

	useEffect(() => {
		return GlobalProctor.addListener(AWSConnectProctorSharedEvents.PROCTOR_CHILD_LOGGED_IN, () => {
			setChildState(APPLICATION_STATE.PROCTOR_CHILD_LOGGED_IN);
		});
	}, []);

	useEffect(() => {
		if (
			GlobalProctor.authenticated &&
			GlobalProctor.contactID &&
			GlobalProctor.contactAttributes?.vLabProctorApplicationURL
		) {
			setChildState(APPLICATION_STATE.INITIALIZING);
			const url = GlobalProctor.contactAttributes.vLabProctorApplicationURL;
			setVlabAppUrl(url);
		} else {
			setVlabAppUrl(undefined);
			clearTimeout(vlabAppLoadedTimeout.current);
		}
		return () => {
			clearTimeout(vlabAppLoadedTimeout.current);
		};
	}, [
		GlobalProctor.authenticated,
		GlobalProctor.contactID,
		GlobalProctor.contactAttributes?.vLabProctorApplicationURL
	]);

	useEffect(() => {
		if (childState === APPLICATION_STATE.INITIALIZING) {
			vlabAppLoadedTimeout.current = setTimeout(() => {
				setChildState(APPLICATION_STATE.APPLICATION_TIMEOUT);
				logger.error(
					"useEffect",
					"Proctor Application is taking longer than expected to load. URL: " + vlabAppUrlRef.current,
					null,
					{
						state: childStateRef.current
					}
				);
				ModalCtx.alert(
					"Proctor Application is taking longer than expected to load. URL: " + vlabAppUrlRef.current,
					"Error"
				);
			}, PROCTOR_APPLICATION_TIMEOUT);
		} else {
			clearTimeout(vlabAppLoadedTimeout.current);
		}
		return () => {
			clearTimeout(vlabAppLoadedTimeout.current);
		};
	}, [childState]);

	const showDeviceWizard = async () => {
		await DeviceWizard.popup(true, TransCtx.lookup);
	};
	const showSpinner =
		childState === "INITIALIZING" || childState === "PROCTOR_CHILD_READY" || childState === "INITIATE_PROCTOR_LOGIN";
	return (
		<SiteContainer title="Proctor Center">
			<div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
				<div className="columns is-gapless" style={{ height: "100%", backgroundColor: "rgb(242, 242, 242)" }}>
					<div
						className="column is-one-quarter is-flex is-flex-direction-column"
						style={{ borderRight: "5px solid #AAAAAA" }}
					>
						<div className="has-background-grey-dark px-2 py-2">
							<div className="buttons is-right">
								<button className="level-item button is-dark is-small is-rounded" onClick={showDeviceWizard}>
									Audio Settings
								</button>
							</div>
						</div>
						{GlobalProctor.authenticated ? null : (
							<div className="is-flex is-align-items-center is-justify-content-center" style={{ height: "100%" }}>
								Please log in first...
							</div>
						)}
						<div
							style={GlobalProctor.authenticated ? { flex: "1" } : { position: "absolute", visibility: "hidden" }}
							ref={GlobalProctor.connectAgentContainerRef}
						/>
					</div>
					{GlobalProctor.authenticated && GlobalProctor.contactID && showSpinner && (
						<div
							style={{
								height: "100%",
								width: "100%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center"
							}}
						>
							<Spinner text={`proctor.${childState}`} />
						</div>
					)}
					{vlabAppUrl ? (
						<div
							key={GlobalProctor.contactID}
							className="column is-flex is-flex-direction-column"
							style={{ overflow: "hidden", backgroundColor: "#FFFFFF" }}
						>
							<iframe
								id="proctor_child"
								ref={GlobalProctor.proctorApplicationRef}
								style={{ width: "100%", flex: 1, backgroundColor: "white", overflow: "auto" }}
								allow="geolocation; microphone; camera; clipboard-read; clipboard-write"
								onError={(e) => {
									logger.error("iframe.onerror", "Proctor Application iFrame error", null, { vlabAppUrl });
								}}
								onLoad={(e) => {
									setChildState(APPLICATION_STATE.IFRAME_LOADED);
									logger.debug("iframe.onload", "Proctor Application Loaded");
								}}
								src={vlabAppUrl}
							></iframe>
						</div>
					) : null}
				</div>
			</div>
		</SiteContainer>
	);
};
